import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { SparklesIcon, CodeBracketIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import toast from 'react-hot-toast'

interface NaturalLanguageQueryProps {
  datasetId: string
  onResultsReceived: (results: any, sql: string) => void
}

export default function NaturalLanguageQuery({ 
  datasetId, 
  onResultsReceived 
}: NaturalLanguageQueryProps) {
  const [question, setQuestion] = useState('')
  const [showSql, setShowSql] = useState(false)
  const [lastQuery, setLastQuery] = useState<{
    question: string
    sql: string
    total: number
    executionTime: number
  } | null>(null)

  const queryMutation = useMutation({
    mutationFn: async (question: string) => {
      const res = await api.post(`/datasets/${datasetId}/rows/nl-query`, {
        question,
        limit: 100,
      })
      return res.data
    },
    onSuccess: (data) => {
      setLastQuery({
        question: data.question,
        sql: data.sql,
        total: data.total,
        executionTime: data.execution_time_ms,
      })
      onResultsReceived(data.rows, data.sql)
      toast.success(`Found ${data.total} results in ${data.execution_time_ms}ms`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to process question')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!question.trim()) {
      toast.error('Please enter a question')
      return
    }
    queryMutation.mutate(question)
  }

  const exampleQuestions = [
    'Show me all rows',
    'Find emails containing gmail',
    'Show users with revenue greater than 1000',
    'Find rows where status is active',
  ]

  return (
    <div className="space-y-4">
      {/* Input Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <SparklesIcon className="h-4 w-4 inline mr-1" />
            Ask a question about your data
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., Show me all gmail users with revenue > $1000..."
              className="input flex-1"
              disabled={queryMutation.isPending}
            />
            <button
              type="submit"
              disabled={queryMutation.isPending || !question.trim()}
              className="btn-primary px-6"
            >
              {queryMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  Query
                </>
              )}
            </button>
          </div>
        </div>

        {/* Example Questions */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-500">Examples:</span>
          {exampleQuestions.map((example, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setQuestion(example)}
              className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 transition-colors"
              disabled={queryMutation.isPending}
            >
              {example}
            </button>
          ))}
        </div>
      </form>

      {/* Query Results Info */}
      {lastQuery && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-900">{lastQuery.total}</span>
                <span>results</span>
              </div>
              <div className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                <span>{lastQuery.executionTime}ms</span>
              </div>
            </div>
            <button
              onClick={() => setShowSql(!showSql)}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
            >
              <CodeBracketIcon className="h-4 w-4" />
              {showSql ? 'Hide SQL' : 'View SQL'}
              {showSql ? (
                <ChevronUpIcon className="h-3 w-3" />
              ) : (
                <ChevronDownIcon className="h-3 w-3" />
              )}
            </button>
          </div>

          {showSql && (
            <div>
              <pre className="text-xs bg-white border border-gray-300 rounded p-3 overflow-x-auto">
                <code className="text-gray-800">{lastQuery.sql}</code>
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
